import React, { useCallback, useEffect, useRef } from 'react'
import { Filter, RotateCcw, RefreshCw } from 'lucide-react'
import { ThemePreset } from '../../theme/presets'

export interface DateFilter {
  from: string
  to: string
}

export type InfiscalMode = '' | 'S' | 'N'

const INFISCAL_OPTIONS: Array<{ value: InfiscalMode; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'S', label: 'Sim' },
  { value: 'N', label: 'Não' },
]

const MIN_DATE = '2022-01-01'
const MAX_DATE = '2026-12-31'

interface FilterBarProps {
  filter: DateFilter
  infiscalMode: InfiscalMode
  vendedor: string
  vendedorOptions: string[]
  onChange: (filter: DateFilter) => void
  onInfiscalChange: (mode: InfiscalMode) => void
  onVendedorChange: (vendedor: string) => void
  onApply: () => void
  onRefresh?: () => void
  refreshing?: boolean
  loading: boolean
  palette: ThemePreset
}

function isoToDisplay(iso: string): string {
  if (!iso) return ''
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

function displayToIso(display: string): string {
  const cleaned = display.replace(/\D/g, '')
  if (cleaned.length < 8) return display
  const d = cleaned.substring(0, 2)
  const m = cleaned.substring(2, 4)
  const y = cleaned.substring(4, 8)
  const iso = `${y}-${m}-${d}`
  // Enforce min/max date bounds
  if (iso < MIN_DATE) return MIN_DATE
  if (iso > MAX_DATE) return MAX_DATE
  return iso
}

function formatDateInput(value: string): string {
  const cleaned = value.replace(/\D/g, '')
  if (cleaned.length <= 2) return cleaned
  if (cleaned.length <= 4) return `${cleaned.substring(0, 2)}/${cleaned.substring(2)}`
  return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/${cleaned.substring(4, 8)}`
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filter,
  infiscalMode,
  vendedor,
  vendedorOptions,
  onChange,
  onInfiscalChange,
  onVendedorChange,
  onApply,
  onRefresh,
  refreshing,
  loading,
  palette,
}) => {
  const fromInputRef = useRef<HTMLInputElement>(null)
  const toInputRef = useRef<HTMLInputElement>(null)

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatDateInput(e.target.value)
      e.target.value = formatted
      if (formatted.length === 10) {
        const iso = displayToIso(formatted)
        onChange({ ...filter, from: iso })
      }
    },
    [filter, onChange]
  )

  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatDateInput(e.target.value)
      e.target.value = formatted
      if (formatted.length === 10) {
        const iso = displayToIso(formatted)
        onChange({ ...filter, to: iso })
      }
    },
    [filter, onChange]
  )

  const handleFromBlur = useCallback(() => {
    if (fromInputRef.current) {
      const formatted = formatDateInput(fromInputRef.current.value)
      if (formatted.length === 10) {
        const iso = displayToIso(formatted)
        fromInputRef.current.value = isoToDisplay(iso)
        onChange({ ...filter, from: iso })
      }
    }
  }, [filter, onChange])

  const handleToBlur = useCallback(() => {
    if (toInputRef.current) {
      const formatted = formatDateInput(toInputRef.current.value)
      if (formatted.length === 10) {
        const iso = displayToIso(formatted)
        toInputRef.current.value = isoToDisplay(iso)
        onChange({ ...filter, to: iso })
      }
    }
  }, [filter, onChange])

  const handleClear = useCallback(() => {
    onChange({ from: '', to: '' })
    onInfiscalChange('')
    onVendedorChange('')
    if (fromInputRef.current) fromInputRef.current.value = ''
    if (toInputRef.current) toInputRef.current.value = ''
    setTimeout(onApply, 0)
  }, [onChange, onInfiscalChange, onVendedorChange, onApply])

  useEffect(() => {
    if (fromInputRef.current) fromInputRef.current.value = isoToDisplay(filter.from)
    if (toInputRef.current) toInputRef.current.value = isoToDisplay(filter.to)
  }, [filter.from, filter.to])

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <Filter className="w-4 h-4" style={{ color: palette.primary }} />
          Filtros
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Data Inicial
          </label>
          <input
            ref={fromInputRef}
            type="text"
            defaultValue={isoToDisplay(filter.from)}
            onChange={handleFromChange}
            onBlur={handleFromBlur}
            placeholder="DD/MM/AAAA"
            maxLength={10}
            inputMode="numeric"
            className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-shadow w-36"
            style={{ ['--tw-ring-color' as never]: `${palette.primary}40` } as React.CSSProperties}
          />
          <span className="text-[10px] text-slate-400 dark:text-slate-500">2022 - 2026</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Data Final
          </label>
          <input
            ref={toInputRef}
            type="text"
            defaultValue={isoToDisplay(filter.to)}
            onChange={handleToChange}
            onBlur={handleToBlur}
            placeholder="DD/MM/AAAA"
            maxLength={10}
            inputMode="numeric"
            className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-shadow w-36"
            style={{ ['--tw-ring-color' as never]: `${palette.primary}40` } as React.CSSProperties}
          />
          <span className="text-[10px] text-slate-400 dark:text-slate-500">2022 - 2026</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Fiscal
          </label>
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden h-9">
            {INFISCAL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onInfiscalChange(opt.value)}
                className={`px-3 text-xs font-medium transition-colors ${
                  infiscalMode === opt.value
                    ? 'text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
                style={
                  infiscalMode === opt.value
                    ? { backgroundColor: palette.primary }
                    : undefined
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {vendedorOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Vendedor
            </label>
            <select
              value={vendedor}
              onChange={(e) => onVendedorChange(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-shadow min-w-[140px]"
              style={{ ['--tw-ring-color' as never]: `${palette.primary}40` } as React.CSSProperties}
            >
              <option value="">Todos</option>
              {vendedorOptions.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={onApply}
          disabled={loading}
          className="h-9 px-5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          style={{ backgroundColor: palette.primary }}
        >
          {loading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Carregando...
            </>
          ) : (
            'Aplicar'
          )}
        </button>

        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="h-9 px-4 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ backgroundColor: '#059669' }}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar Dados'}
          </button>
        )}

        <button
          onClick={handleClear}
          disabled={loading}
          className="h-9 px-4 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Limpar
        </button>
      </div>
    </div>
  )
}
